import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, Loader2, Mountain, Eye, EyeOff, ChevronDown, MapPin, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import ImageDropUpload from '../../components/admin/ImageDropUpload';
import { invalidateSettingsCache } from '../../lib/settingsCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankTrail = () => ({
  name: '',
  description: '',
  image_url: '',
  place_name: '',
  address: '',
  map_url: '',
  map_embed_url: '',
  parks: 'Virgin Islands National Park',
  skill: '',
  duration: '',
  reservations: 'No',
  pets: 'Yes',
  time_of_day: 'Day, Dawn, Dusk',
  sort_order: 0,
  is_active: true,
});

const TrailEditor = ({ trail, onChange, onSave, onDelete, saving, isOpen, onToggle }) => {
  const id = trail.id || 'new';
  return (
    <Card data-testid={`hiking-trail-editor-${id}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={onToggle}
              className="p-1 rounded text-slate-500 hover:text-blue-700 hover:bg-slate-100 transition-colors"
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse trail' : 'Expand trail'}
              data-testid={`hiking-accordion-${id}`}
            >
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <Mountain className="w-5 h-5 text-blue-600 shrink-0" />
            <button type="button" onClick={onToggle} className="text-left min-w-0">
              <CardTitle className="text-base truncate">{trail.name || 'New Trail'}</CardTitle>
            </button>
            {trail.is_active ? (
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> Visible
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> Hidden
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onChange({ ...trail, is_active: !trail.is_active })}
              data-testid={`hiking-toggle-active-${id}`}>
              {trail.is_active ? 'Hide' : 'Show'}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid={`hiking-save-${id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={onDelete}
              data-testid={`hiking-delete-${id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          {trail.skill || 'no skill set'} &middot; {trail.duration || 'no duration'} &middot; order {trail.sort_order ?? 0}
          {!isOpen && ' \u2014 click the chevron to edit'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4" hidden={!isOpen}>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Trail Name</Label>
            <Input value={trail.name} onChange={(e) => onChange({ ...trail, name: e.target.value })}
              placeholder="e.g. Annaberg Ruins Trail" className="mt-1" data-testid={`hiking-name-${id}`} />
          </div>
          <div>
            <Label>Order Number</Label>
            <Input type="number" value={trail.sort_order}
              onChange={(e) => onChange({ ...trail, sort_order: Number(e.target.value) || 0 })}
              className="mt-1" data-testid={`hiking-order-${id}`} />
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Textarea value={trail.description} onChange={(e) => onChange({ ...trail, description: e.target.value })}
            rows={3} className="mt-1" data-testid={`hiking-description-${id}`} />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Place Name</Label>
            <Input value={trail.place_name} onChange={(e) => onChange({ ...trail, place_name: e.target.value })}
              placeholder="Annaberg Area Trail" className="mt-1" data-testid={`hiking-place-${id}`} />
          </div>
          <div>
            <Label>Address</Label>
            <Input value={trail.address} onChange={(e) => onChange({ ...trail, address: e.target.value })}
              placeholder="St John 00830, USVI" className="mt-1" data-testid={`hiking-address-${id}`} />
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Map Link</Label>
          <Input value={trail.map_url} onChange={(e) => onChange({ ...trail, map_url: e.target.value })}
            placeholder="https://maps.google.com/?cid=..." className="mt-1" data-testid={`hiking-map-${id}`} />
          <p className="text-xs text-gray-500 mt-1">Powers the &ldquo;Open in Maps&rdquo; button. Leave blank to hide it.</p>
        </div>

        <div>
          <Label>Google Maps Embed URL</Label>
          <Input value={trail.map_embed_url || ''} onChange={(e) => onChange({ ...trail, map_embed_url: e.target.value })}
            placeholder="https://www.google.com/maps/embed?pb=..." className="mt-1" data-testid={`hiking-embed-${id}`} />
          <p className="text-xs text-gray-500 mt-1">Shows a &ldquo;View Map&rdquo; button that opens the map in a pop-up.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Parks</Label>
            <Input value={trail.parks} onChange={(e) => onChange({ ...trail, parks: e.target.value })}
              className="mt-1" data-testid={`hiking-parks-${id}`} />
          </div>
          <div>
            <Label>Skill</Label>
            <Input value={trail.skill} onChange={(e) => onChange({ ...trail, skill: e.target.value })}
              placeholder="Easy / Moderate" className="mt-1" data-testid={`hiking-skill-${id}`} />
          </div>
          <div>
            <Label>Duration</Label>
            <Input value={trail.duration} onChange={(e) => onChange({ ...trail, duration: e.target.value })}
              placeholder="30 minutes - 0.2 miles" className="mt-1" data-testid={`hiking-duration-${id}`} />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Reservations</Label>
            <Input value={trail.reservations} onChange={(e) => onChange({ ...trail, reservations: e.target.value })}
              className="mt-1" data-testid={`hiking-reservations-${id}`} />
          </div>
          <div>
            <Label>Pets</Label>
            <Input value={trail.pets} onChange={(e) => onChange({ ...trail, pets: e.target.value })}
              className="mt-1" data-testid={`hiking-pets-${id}`} />
          </div>
          <div>
            <Label>Time Of Day</Label>
            <Input value={trail.time_of_day} onChange={(e) => onChange({ ...trail, time_of_day: e.target.value })}
              className="mt-1" data-testid={`hiking-time-${id}`} />
          </div>
        </div>

        <div className="max-w-md">
          <ImageDropUpload
            label="Trail Photo"
            hint="landscape works best"
            value={trail.image_url}
            folder="hiking/trails"
            aspect="aspect-video"
            objectFit="object-cover"
            testId={`hiking-photo-drop-${id}`}
            onUploaded={(url) => onChange({ ...trail, image_url: url })}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const listToText = (items) => (Array.isArray(items) ? items.join('\n') : '');
const textToList = (text) => (text || '').split('\n').map((l) => l.trim()).filter(Boolean);

const AdminHikingTrails = () => {
  const [trails, setTrails] = useState([]);
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [savingPage, setSavingPage] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const bustPublicCache = () =>
    invalidateSettingsCache(`${process.env.REACT_APP_BACKEND_URL}/api/public/hiking`);

  const load = async () => {
    try {
      const [tRes, pRes] = await Promise.all([
        axios.get(`${API}/hiking/trails`, { headers: authHeaders() }),
        axios.get(`${API}/hiking/page`, { headers: authHeaders() }),
      ]);
      setTrails(Array.isArray(tRes.data) ? tRes.data : []);
      setPage(pRes.data || {});
    } catch (error) {
      toast({
        title: 'Load failed',
        description: error.response?.data?.detail || 'Could not load hiking trails.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveTrail = async (trail) => {
    if (!trail.name?.trim()) {
      toast({ title: 'Name required', description: 'Give the trail a name first.', variant: 'destructive' });
      return;
    }
    setSavingId(trail.id || 'new');
    try {
      const payload = { ...trail, sort_order: Number(trail.sort_order) || 0, is_active: Boolean(trail.is_active) };
      delete payload.id; delete payload.slug; delete payload.created_at; delete payload.updated_at;
      if (trail.id) {
        await axios.put(`${API}/hiking/trails/${trail.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/hiking/trails`, payload, { headers: authHeaders() });
        setDraft(null);
      }
      bustPublicCache();
      toast({ title: 'Saved', description: `${payload.name} updated.` });
      await load();
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save the trail.',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const deleteTrail = async (trail) => {
    if (!trail.id) { setDraft(null); return; }
    if (!window.confirm(`Delete "${trail.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/hiking/trails/${trail.id}`, { headers: authHeaders() });
      bustPublicCache();
      toast({ title: 'Deleted', description: `${trail.name} removed.` });
      await load();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Could not delete the trail.',
        variant: 'destructive',
      });
    }
  };

  const savePage = async () => {
    setSavingPage(true);
    try {
      const { data } = await axios.put(`${API}/hiking/page`, page, { headers: authHeaders() });
      setPage(data || {});
      bustPublicCache();
      toast({ title: 'Saved', description: 'Page content updated.' });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save page content.',
        variant: 'destructive',
      });
    } finally {
      setSavingPage(false);
    }
  };

  if (loading || !page) {
    return (
      <div className="flex justify-center py-24" data-testid="hiking-admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="admin-hiking-trails">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mountain className="w-7 h-7 text-blue-600" />
            Hiking St John
          </h2>
          <p className="text-gray-500 mt-1">
            {trails.length} trail{trails.length === 1 ? '' : 's'}. Turn the component on or off with
            the <strong>hiking_enabled</strong> feature flag.
          </p>
        </div>
        {!draft && (
          <Button onClick={() => { setDraft(blankTrail()); setOpenId('new'); }} data-testid="hiking-add-trail">
            <Plus className="w-4 h-4 mr-2" /> Add Trail
          </Button>
        )}
      </div>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Page Content</CardTitle>
              <CardDescription>Headings, intro copy and the two safety lists (one item per line).</CardDescription>
            </div>
            <Button size="sm" onClick={savePage} disabled={savingPage} data-testid="hiking-save-page">
              {savingPage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Content
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Intro Title</Label>
              <Input value={page.intro_title || ''} onChange={(e) => setPage({ ...page, intro_title: e.target.value })}
                className="mt-1" data-testid="hiking-intro-title" />
            </div>
            <div>
              <Label>Trails Section Title</Label>
              <Input value={page.trails_title || ''} onChange={(e) => setPage({ ...page, trails_title: e.target.value })}
                className="mt-1" data-testid="hiking-trails-title" />
            </div>
          </div>
          <div>
            <Label>Intro Body</Label>
            <Textarea value={page.intro_body || ''} onChange={(e) => setPage({ ...page, intro_body: e.target.value })}
              rows={5} className="mt-1" data-testid="hiking-intro-body" />
          </div>
          <div>
            <Label>Full Hiking Locations Map link (optional)</Label>
            <Input value={page.full_map_url || ''} onChange={(e) => setPage({ ...page, full_map_url: e.target.value })}
              className="mt-1" data-testid="hiking-full-map" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>{page.hike_safely_title || 'Hike Safely'} items</Label>
              <Textarea value={listToText(page.hike_safely_items)}
                onChange={(e) => setPage({ ...page, hike_safely_items: textToList(e.target.value) })}
                rows={8} className="mt-1" data-testid="hiking-safely-items" />
            </div>
            <div>
              <Label>{page.trail_safety_title || 'Trail Safety'} items</Label>
              <Textarea value={listToText(page.trail_safety_items)}
                onChange={(e) => setPage({ ...page, trail_safety_items: textToList(e.target.value) })}
                rows={8} className="mt-1" data-testid="hiking-safety-items" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {draft && (
          <TrailEditor
            trail={draft} onChange={setDraft} onSave={() => saveTrail(draft)} onDelete={() => setDraft(null)}
            saving={savingId === 'new'} isOpen={openId === 'new'}
            onToggle={() => setOpenId(openId === 'new' ? null : 'new')}
          />
        )}

        {trails.map((trail) => (
          <TrailEditor
            key={trail.id}
            trail={trail}
            onChange={(next) => setTrails((prev) => prev.map((t) => (t.id === next.id ? next : t)))}
            onSave={() => saveTrail(trail)}
            onDelete={() => deleteTrail(trail)}
            saving={savingId === trail.id}
            isOpen={openId === trail.id}
            onToggle={() => setOpenId(openId === trail.id ? null : trail.id)}
          />
        ))}

        {!draft && trails.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              No trails yet. Click <strong>Add Trail</strong> to create the first one.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminHikingTrails;
